"use client";

import { Image as ImageType } from "@/types/dto/common/image";
import { UnforgettableMoments } from "@/types/dto/destinations-page/destination-page-types";
import { Tour } from "@/types/dto/tours/tour";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { SectionHeader } from "@/components/common/section-header";
import sanitizeHtml from "@/utils/sanitize-html";
import {ColletteButton} from "@collette-travel/collette-common-controls";

export interface UnforgettableMomentsProps {
  moments: UnforgettableMoments;
}
interface MiniCardProps {
  title: string;
  image: ImageType;
  copy: string;
  tour?: Tour;
  index: number;
}

const MiniCard = ({ title, image, copy, tour, index }: MiniCardProps) => {
  if (!tour?.tourUrl) return null;

  return (
    <Link
      href={tour.tourUrl}
      passHref
      data-testid={`moments-card-link-${index}`}
      data-track="click"
      data-tour-name={tour.title}
      data-tour-price={(tour.price || "").toString().replace(/[^0-9]/g, "")}
      data-tour-duration={String(tour.days)}
      data-tour-id={(tour as any).neoId}
      data-carousel-position={String(index + 1)}
    >
      <Card
        component="div"
        sx={{
          borderRadius: "2rem",
          boxShadow: 6,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          textDecoration: "none",
          overflow: "hidden",
          backgroundColor: "background.default",
          alignItems: "stretch"
        }}
      >
          {image?.imageUrl && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 218
              }}
            >
              <Image
                src={image.imageUrl || ""}
                alt={image.altText || "Tour image"}
                fill
                style={{ objectFit: "cover" }}
                data-testid={`moments-card-image-${index}`} // Unique test ID for the image
              />
            </Box>
          )}

          <CardContent
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: "1px solid",
              borderColor: "divider",
              borderTop: "none",
              borderBottomLeftRadius: 2,
              borderBottomRightRadius: 2,
              padding: 2.5,
              borderRadius: "0 0 2rem 2rem"
            }}
          >
            <Box sx={{ mb: 1 }} component="div">
              {title && (
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ color: "text.primary", mb: 1, height: 48, lineHeight: "normal" }}
                >
                  {title}
                </Typography>
              )}
              {copy && (
                <Typography
                  component="div"
                  variant="subtitle1"
                  sx={{
                    color: "text.primary",
                    fontWeight: "500",
                    // Responsive typography styles based on Figma specs
                    fontSize: { xs: "1.6rem", md: "1.8rem" }
                  }}
                >
                  {typeof copy === "string" ? (
                    // eslint-disable-next-line react/no-danger
                    <div
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(copy) }}
                      className="moments-card-content"
                      data-testid={`moments-card-copy-${index}`}
                    />
                  ) : (
                    copy
                  )}
                </Typography>
              )}
            </Box>

            {tour.days && (
              <ColletteButton
                component="div"
                variant="contained"
                color="primary"
                size="large"
                testId={`moments-card-button-${index}`} // Unique test ID for the button
                label={`View the ${tour.days}-Day Itinerary`}
              />
            )}
          </CardContent>
      </Card>
    </Link>
  );
};

export const UnforgettableMomentsComponent: React.FC<UnforgettableMomentsProps> = ({ moments }) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  return (
    <Box
      sx={{ paddingTop: "3.2rem", marginBottom: "6rem" }}
      id="moments"
      className="anchor-section"
      data-track="visibility"
      data-parent-container-type="cards"
      data-module-name="Unforgettable Moments Section"
      data-ranking-algorithm="manual"
      data-module-ranking="custom_sort"
      data-module-ranking-position="9"
      component="section"
    >
      <SectionHeader
        headerText={moments.basicInfo.headerText}
        copyText={moments.basicInfo.copyText || ""}
        headerTestId="unforgettable-moments-header"
        copyTestId="unforgettable-moments-copy"
        sx={{ paddingBottom: "2.5rem" }}
      />

      <Grid container spacing={3} alignItems="stretch">
        {moments.tourCards.map((card, index) => {
          const totalCards = moments.tourCards.length;

          // Dynamically assign column width
          let mdCols;

          if (totalCards === 1) {
            mdCols = 12;
          } else if (totalCards === 2) {
            mdCols = 6;
          } else if (totalCards === 3) {
            mdCols = isLgUp ? 4 : 6; // 3 cards at ≥1200px, else show 2 cards/row
          } else {
            mdCols = isLgUp ? 3 : 6; // 4+ cards: 3+ per row at ≥1200px, 2 per row otherwise
          }

          return (
            <Grid size={{
              xs: 12,
              sm: 6,
              md: mdCols
            }} key={card.tour?.id ?? index}>
              <MiniCard
                title={card.title}
                image={card.image}
                copy={card.copy}
                tour={card.tour}
                index={index}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
